import { useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { PAYMENT_METHODS, formatQty, variantLabel, type PaymentMethod, type UnitType } from '@pos/core';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { BarChart } from '../../ui/BarChart.tsx';
import { Alert, Button, Card, Input, PageHeader, Select, Spinner, Stat, cx, errorMessage } from '../../ui/index.tsx';
import { ChartIcon, ReceiptIcon, TagIcon, TrendIcon } from '../../ui/icons.tsx';
import { useCatalog, useMoney } from '../products/data.ts';
import { useStockMap, useStockRows } from '../stock/data.ts';
import { useBalances } from '../customers/data.ts';
import { fetchSummary, fetchTopProducts, fillDays, useReport, type ReportSummary } from './data.ts';
import { downloadWorkbook, fetchSalesForExport, fullExport, salesSheets } from './export.ts';
import { PERIOD_LABELS, localDate, makePeriod, presetPeriod, type PeriodKey } from './period.ts';

const shortDay = new Intl.DateTimeFormat('es-AR', { day: 'numeric', month: 'numeric', timeZone: 'UTC' });
const longDay = new Intl.DateTimeFormat('es-AR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
const pct = (part: number, whole: number) => (whole ? `${Math.round((part / whole) * 100)} %` : '—');

/** Reportes del dueño: ventas, ganancia, medios de pago, horarios, vendedores, categorías, productos, inventario y exportes. */
export function ReportsPage() {
  const { business } = useSession();
  const money = useMoney();
  const tz = business.timezone || 'America/Argentina/Buenos_Aires';
  const today = localDate(new Date(), tz);
  const [key, setKey] = useState<PeriodKey>('last7');
  const [custom, setCustom] = useState({ from: today, to: today });
  const [order, setOrder] = useState<'total' | 'qty' | 'profit'>('total');

  const period = useMemo(() => {
    if (key !== 'custom') return presetPeriod(key, tz);
    const [a, b] = custom.from <= custom.to ? [custom.from, custom.to] : [custom.to, custom.from];
    return makePeriod(a, b, tz);
  }, [key, custom, tz]);

  const summary = useReport((id) => fetchSummary(id, period), [period.from, period.to]);
  const top = useReport((id) => fetchTopProducts(id, period, order, 50), [period.from, period.to, order]);

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      <PageHeader title="Reportes" description="Suman las ventas de todos tus dispositivos. Necesitan conexión." />
      <div className="flex flex-wrap items-end gap-2">
        <div className="w-48">
          <Select value={key} onChange={(e) => setKey(e.target.value as PeriodKey)} aria-label="Período">
            {Object.entries(PERIOD_LABELS).map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </Select>
        </div>
        {key === 'custom' && (
          <>
            <div className="w-44">
              <Input type="date" value={custom.from} max={today} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} aria-label="Desde" />
            </div>
            <div className="w-44">
              <Input type="date" value={custom.to} max={today} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} aria-label="Hasta" />
            </div>
          </>
        )}
        <p className="pb-2 text-sm text-slate-500">
          {period.firstDay === period.lastDay ? longDay.format(new Date(`${period.firstDay}T12:00:00Z`)) : `${shortDay.format(new Date(`${period.firstDay}T12:00:00Z`))} al ${shortDay.format(new Date(`${period.lastDay}T12:00:00Z`))}`}
        </p>
      </div>

      {summary.state === 'loading' && (
        <div className="flex justify-center py-10 text-slate-500">
          <Spinner />
        </div>
      )}
      {summary.state === 'offline' && <Alert tone="warning">Sin conexión: los reportes suman todos los dispositivos y necesitan internet.</Alert>}
      {summary.state === 'error' && <Alert tone="error">No se pudo cargar el reporte: {summary.message}</Alert>}
      {summary.state === 'ready' && <SummaryView data={summary.data} days={fillDays(period, summary.data.by_day)} money={money} />}

      {summary.state === 'ready' && (
        <Card flush>
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-2">
            <h2 className="font-semibold">Productos</h2>
            <div className="w-48">
              <Select compact value={order} onChange={(e) => setOrder(e.target.value as typeof order)} aria-label="Ordenar productos">
                <option value="total">Por importe vendido</option>
                <option value="qty">Por cantidad</option>
                <option value="profit">Por ganancia</option>
              </Select>
            </div>
          </div>
          {top.state !== 'ready' ? (
            <div className="flex justify-center p-6 text-slate-500">{top.state === 'error' ? top.message : <Spinner />}</div>
          ) : top.data.length === 0 ? (
            <p className="p-6 text-center text-sm text-slate-500">Sin ventas en el período.</p>
          ) : (
            <Table
              head={['Producto', 'Cantidad', 'Vendido', 'Ganancia']}
              rows={top.data.map((p) => {
                const attrs = variantLabel(p.attributes);
                return [`${p.product}${attrs ? ` · ${attrs}` : ''}`, formatQty(p.qty_milli, p.unit_type), money(p.total), p.profit === null ? 'sin costo' : p.profit < 0 ? `${money(p.profit)} ⚠ pérdida` : money(p.profit)];
              })}
            />
          )}
        </Card>
      )}

      <InventoryValuation money={money} />
      <Exports period={period} />
    </div>
  );
}

function SummaryView({ data, days, money }: { data: ReportSummary; days: ReportSummary['by_day']; money: (c: number) => string }) {
  const t = data.totals;
  const [asTable, setAsTable] = useState(false);
  const hasCosts = t.revenue_with_cost > 0;
  const methodTotal = data.by_method.reduce((a, m) => a + Math.max(0, m.total), 0);
  const hours = Array.from({ length: 24 }, (_, h) => data.by_hour.find((x) => x.hour === h) ?? { hour: h, total: 0, count: 0 });
  const firstHour = hours.findIndex((h) => h.count > 0);
  const lastHour = 23 - [...hours].reverse().findIndex((h) => h.count > 0);
  const visibleHours = firstHour === -1 ? [] : hours.slice(firstHour, lastHour + 1);

  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Vendido (neto)" icon={ChartIcon} value={money(t.total)} foot={`${t.sales_count} ventas · ${t.returns_count} devoluciones`} />
        <Stat
          label="Ganancia"
          icon={TrendIcon}
          tone="green"
          value={hasCosts ? money(t.profit) : '—'}
          foot={hasCosts ? `Margen ${pct(t.profit, t.revenue_with_cost)} sobre lo vendido con costo` : 'Cargá costos para verla'}
        />
        <Stat label="Ticket promedio" icon={ReceiptIcon} value={money(t.sales_count ? Math.round(t.sales_total / t.sales_count) : 0)} foot={`IVA incluido en las ventas: ${money(t.tax)}`} />
        <Stat label="Descuentos y ajustes" icon={TagIcon} tone="amber" value={money(t.discounts)} foot={`Recargos/desc. por medio de pago: ${money(t.adjustments)}`} />
      </div>
      {hasCosts && t.profit < 0 && (
        <Alert tone="warning">
          La ganancia da negativa: hay productos que se venden por debajo de su costo. Mirá la tabla de productos ordenada por ganancia y revisá sus costos (por ejemplo, si se cargó el costo de una caja como si fuera de una unidad).
        </Alert>
      )}
      {t.products_without_cost > 0 && (
        <Alert tone="info">
          {t.products_without_cost} productos vendidos no tienen costo cargado ({money(t.revenue_without_cost)} de ventas): no entran en la ganancia. Cargales el costo en Productos.
        </Alert>
      )}

      <Card>
        <div className="mb-2 flex items-baseline justify-between">
          <h2 className="font-semibold">Ventas por día</h2>
          <button type="button" className="text-sm font-medium text-brand" onClick={() => setAsTable((v) => !v)}>
            {asTable ? 'Ver gráfico' : 'Ver tabla'}
          </button>
        </div>
        {asTable ? (
          <Table
            head={['Día', 'Ventas', 'Vendido', 'Ganancia']}
            rows={days.map((d) => [longDay.format(new Date(`${d.day}T12:00:00Z`)), String(d.count), money(d.total), hasCosts ? money(d.profit) : '—'])}
          />
        ) : (
          <BarChart
            ariaLabel="Vendido por día"
            format={money}
            bars={days.map((d) => ({
              key: d.day,
              label: shortDay.format(new Date(`${d.day}T12:00:00Z`)),
              value: d.total,
              tooltip: `${longDay.format(new Date(`${d.day}T12:00:00Z`))}: ${money(d.total)} · ${d.count} ventas`,
            }))}
          />
        )}
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <h2 className="mb-3 font-semibold">Medios de pago</h2>
          {data.by_method.length === 0 ? (
            <p className="text-sm text-slate-500">Sin ventas.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {data.by_method.map((m) => (
                <li key={m.method}>
                  <div className="flex justify-between">
                    <span>{PAYMENT_METHODS[m.method as PaymentMethod]?.label ?? m.method}</span>
                    <span className="tabular-nums">
                      {money(m.total)} <span className="text-slate-500">({pct(Math.max(0, m.total), methodTotal)})</span>
                    </span>
                  </div>
                  <div className="mt-1 h-2 rounded-full bg-slate-100">
                    <div className="chart-fill h-2 rounded-full" style={{ width: `${methodTotal ? Math.max(0, (m.total / methodTotal) * 100) : 0}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card>
          <h2 className="mb-2 font-semibold">Ventas por hora</h2>
          {visibleHours.length === 0 ? (
            <p className="text-sm text-slate-500">Sin ventas.</p>
          ) : (
            <BarChart
              ariaLabel="Vendido por hora del día"
              format={money}
              height={160}
              bars={visibleHours.map((h) => ({ key: String(h.hour), label: `${h.hour}h`, value: h.total, tooltip: `${h.hour}:00 a ${h.hour}:59: ${money(h.total)} · ${h.count} ventas` }))}
            />
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card flush>
          <h2 className="border-b border-slate-100 px-4 py-2 font-semibold">Por vendedor</h2>
          <Table head={['Vendedor', 'Ventas', 'Vendido', 'Descuentos']} rows={data.by_seller.map((s) => [s.name, String(s.count), money(s.total), money(s.discounts)])} />
        </Card>
        <Card flush>
          <h2 className="border-b border-slate-100 px-4 py-2 font-semibold">Por categoría</h2>
          <Table head={['Categoría', 'Vendido', 'Ganancia', '% del total']} rows={data.by_category.map((c) => [c.name, money(c.total), hasCosts ? money(c.profit) : '—', pct(c.total, t.items_total)])} />
        </Card>
      </div>
    </>
  );
}

/** Valor del inventario por categoría (datos de este dispositivo: costos y stock ya bajados). */
function InventoryValuation({ money }: { money: (c: number) => string }) {
  const catalog = useCatalog();
  const stock = useStockMap();
  const rows = useStockRows(catalog, stock);
  const groups = useMemo(() => {
    const map = new Map<string, { name: string; cost: number; retail: number; withoutCost: number; products: number }>();
    for (const r of rows) {
      if (!r.tracked || r.qty <= 0 || !r.item.product.active || !r.variant.active) continue;
      const name = r.item.category?.name ?? 'Sin categoría';
      const g = map.get(name) ?? { name, cost: 0, retail: 0, withoutCost: 0, products: 0 };
      g.products++;
      g.retail += Math.round((r.qty * r.variant.price) / 1000);
      if (r.cost === undefined) g.withoutCost++;
      else g.cost += Math.round((r.qty * r.cost) / 1000);
      map.set(name, g);
    }
    return [...map.values()].sort((a, b) => b.retail - a.retail);
  }, [rows]);
  const total = groups.reduce((a, g) => ({ cost: a.cost + g.cost, retail: a.retail + g.retail }), { cost: 0, retail: 0 });

  return (
    <Card flush>
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-slate-100 px-4 py-2">
        <h2 className="font-semibold">Valor del inventario (hoy)</h2>
        <p className="text-sm text-slate-500">
          A costo {money(total.cost)} · a precio de venta {money(total.retail)}
        </p>
      </div>
      {!catalog || !stock ? (
        <div className="flex justify-center p-6 text-slate-500">
          <Spinner />
        </div>
      ) : groups.length === 0 ? (
        <p className="p-6 text-center text-sm text-slate-500">No hay productos con stock cargado.</p>
      ) : (
        <Table
          head={['Categoría', 'Productos', 'A costo', 'A precio de venta', 'Ganancia posible']}
          rows={groups.map((g) => [
            g.name + (g.withoutCost ? ` (${g.withoutCost} sin costo)` : ''),
            String(g.products),
            money(g.cost),
            money(g.retail),
            money(g.retail - g.cost),
          ])}
        />
      )}
    </Card>
  );
}

function Exports({ period }: { period: ReturnType<typeof makePeriod> }) {
  const { business } = useSession();
  const catalog = useCatalog();
  const stock = useStockMap();
  const rows = useStockRows(catalog, stock);
  const balances = useBalances();
  const members = useLiveQuery(() => db.memberships.where('business_id').equals(business.id).toArray(), [business.id]);
  const customers = useLiveQuery(() => db.customers.where('business_id').equals(business.id).toArray(), [business.id]);
  const sessions = useLiveQuery(() => db.cash_sessions.where('business_id').equals(business.id).toArray(), [business.id]);
  const devices = useLiveQuery(() => db.devices.where('business_id').equals(business.id).toArray(), [business.id]);
  const registers = useLiveQuery(() => db.registers.where('business_id').equals(business.id).toArray(), [business.id]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const tz = business.timezone || 'America/Argentina/Buenos_Aires';
  const stamp = new Date().toISOString().slice(0, 10);
  const person = (id: string | null | undefined) => members?.find((m) => m.user_id === id)?.display_name ?? '—';
  const pesos = (c: number | null | undefined) => (c == null ? null : c / 100);

  const run = async (name: string, action: () => Promise<void>) => {
    setBusy(name);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const unitOf = (variantId: string): UnitType => {
    for (const item of catalog?.items ?? []) if (item.variants.some((v) => v.id === variantId)) return item.product.unit_type;
    return 'unit';
  };

  return (
    <Card className="space-y-3">
      <h2 className="font-semibold">Exportar a Excel</h2>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          loading={busy === 'sales'}
          onClick={() =>
            run('sales', async () => {
              if (!navigator.onLine) throw new Error('Para exportar las ventas hace falta conexión.');
              const sales = await fetchSalesForExport(business.id, period.from, period.to, unitOf);
              const names = { person, customer: (id: string | null | undefined) => customers?.find((c) => c.id === id)?.name ?? '' };
              await downloadWorkbook(`ventas-${period.firstDay}-al-${period.lastDay}.xlsx`, salesSheets(sales, names, tz));
            })
          }
        >
          Ventas del período
        </Button>
        <Button
          variant="secondary"
          loading={busy === 'stock'}
          onClick={() =>
            run('stock', () =>
              downloadWorkbook(`productos-y-stock-${stamp}.xlsx`, [
                {
                  name: 'Productos',
                  rows: rows.map((r) => ({
                    Producto: r.label,
                    Categoría: r.item.category?.name ?? '',
                    'Código de barras': r.variant.barcode ?? '',
                    'Código interno': r.variant.sku ?? '',
                    Precio: pesos(r.variant.price),
                    Costo: pesos(r.cost),
                    Stock: r.tracked ? r.qty / 1000 : null,
                    Unidad: r.item.product.unit_type === 'unit' ? 'u' : formatQty(1000, r.item.product.unit_type).replace(/^1\s?/, ''),
                    'Stock mínimo': r.variant.min_stock == null ? null : r.variant.min_stock / 1000,
                    Activo: r.item.product.active && r.variant.active ? 'Sí' : 'No',
                  })),
                },
              ]),
            )
          }
        >
          Productos y stock
        </Button>
        <Button
          variant="secondary"
          loading={busy === 'customers'}
          onClick={() =>
            run('customers', () =>
              downloadWorkbook(`clientes-${stamp}.xlsx`, [
                {
                  name: 'Clientes',
                  rows: (customers ?? [])
                    .filter((c) => !c.deleted_at)
                    .map((c) => ({
                      Nombre: c.name,
                      Teléfono: c.phone ?? '',
                      Email: c.email ?? '',
                      Documento: c.doc_type ? `${c.doc_type} ${c.doc_number}` : '',
                      Dirección: c.address ?? '',
                      'Saldo (debe)': pesos(balances?.get(c.id) ?? 0),
                      'Límite de crédito': pesos(c.credit_limit),
                      Activo: c.active ? 'Sí' : 'No',
                    })),
                },
              ]),
            )
          }
        >
          Clientes y saldos
        </Button>
        <Button
          variant="secondary"
          loading={busy === 'cash'}
          onClick={() =>
            run('cash', () =>
              downloadWorkbook(`cajas-${stamp}.xlsx`, [
                {
                  name: 'Cajas',
                  rows: [...(sessions ?? [])]
                    .sort((a, b) => a.opened_at.localeCompare(b.opened_at))
                    .map((s) => ({
                      Caja: registers?.find((r) => r.id === s.register_id)?.name ?? registers?.find((r) => r.is_default)?.name ?? '',
                      'Solo un dispositivo': s.device_only ? 'Sí' : 'No',
                      'Abierta en': devices?.find((d) => d.id === s.device_id)?.name ?? '',
                      Apertura: new Date(s.opened_at).toLocaleString('es-AR', { timeZone: tz }),
                      Abrió: person(s.opened_by),
                      'Fondo inicial': pesos(s.opening_amount),
                      Cierre: s.closed_at ? new Date(s.closed_at).toLocaleString('es-AR', { timeZone: tz }) : 'Abierta',
                      Cerró: s.closed_by ? person(s.closed_by) : '',
                      Esperado: pesos(s.expected_cash),
                      Contado: pesos(s.counted_cash),
                      Diferencia: pesos(s.difference),
                      Notas: s.close_notes ?? '',
                    })),
                },
              ]),
            )
          }
        >
          Cajas
        </Button>
      </div>
      <div className="border-t border-slate-100 pt-3">
        <FullExportButton />
      </div>
      {error && <Alert tone="error">{error}</Alert>}
    </Card>
  );
}

/** Descarga completa de los datos del negocio (también disponible con la suscripción suspendida). */
export function FullExportButton() {
  const { business } = useSession();
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          loading={progress !== null}
          onClick={async () => {
            setError(null);
            setProgress('');
            try {
              await fullExport(business.id, business.name, db, (t) => setProgress(t));
            } catch (e) {
              setError(errorMessage(e));
            } finally {
              setProgress(null);
            }
          }}
        >
          Descargar todos mis datos
        </Button>
        <span className={cx('text-sm text-slate-500', progress && 'text-slate-700')}>
          {progress ? `Bajando: ${progress}…` : 'Un Excel con todo lo que cargaste: productos, ventas, stock, caja, clientes y más.'}
        </span>
      </div>
      {error && <Alert tone="error">{error}</Alert>}
    </div>
  );
}

function Table({ head, rows }: { head: string[]; rows: string[][] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-slate-500">
            {head.map((h, i) => (
              <th key={h} className={cx('px-4 py-2 font-medium', i > 0 && 'text-right')}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => (
                <td key={j} className={cx('px-4 py-2', j > 0 ? 'text-right tabular-nums whitespace-nowrap' : 'min-w-40')}>
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
